import TranslateIcon from "@mui/icons-material/Translate";
import TvIcon from "@mui/icons-material/Tv";
import {
  Chip,
  CircularProgress,
  Dialog,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AppLoader from "../../../../components/AppLoader";
import DialogBody from "../../../../components/Dialog/Body";
import DialogActionButtons from "../../../../components/Dialog/Footer";
import DialogHeader from "../../../../components/Dialog/Header";
import Label from "../../../../components/Label";
import TextInput from "../../../../components/TextInputs";
import { setCharityOrganizationCurrentPage } from "../../../../lib/slices/pagination/charityOrganizationPageSlice";
import {
  createCharityOrganization,
  updateCharityOrganization,
} from "../../../../services/charityOrganizationApiFn";
import { capitalizeText, handleOpenSnackBar } from "../../../../utils";
import { translateText } from "../../../../utils/translationService";
import { addEditCharityOrganizationValidation } from "../../../../utils/Validation";
import UploadBox from "../../../../components/UploadBox";
import { APP_IMAGES } from "../../../../assets";
import axios from "axios";
import { baseUrl } from "../../../../services";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(4, 2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
}));

let paramsData = {};

const initialInputValues = {
  logo: null,
  name_en: "",
  name_ro: "",
  description_en: "",
  description_ro: "",
  order: 0,
  websiteUrl: "",
};

export const AddEditCharityOrganizationDialog = forwardRef(({}, ref) => {
  // Ref for Upload Logo Input
  const openLogoFile = React.useRef(null);
  // Dispatch Hook
  const dispatch = useDispatch();
  // Translation Hook
  const { t } = useTranslation(["catalogue", "common"]);
  // Open State
  const [open, setOpen] = useState(false);
  // Loading State
  const [isLoading, setIsLoading] = useState({
    save: false,
    translateToEnglish: false,
    logo: false,
  });
  // Input Error State
  const [inputError, setInputError] = useState({});
  // Input Values State
  const [inputValues, setInputValues] = useState(initialInputValues);
  // Logo File State
  const [logoFile, setLogoFile] = useState(null);
  // Track logo change state
  const [isLogoChanged, setIsLogoChanged] = useState(false);
  // Get Search Keys State
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  // Get Current Language State
  const currentLanguage = useSelector(
    (state) => state.languageReducer.currentLanguage,
  );
  // Get Pagination State
  const pagination = useSelector(
    (state) => state.charityOrganizationPaginationReducer,
  );

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setOpen(true);
      paramsData = params;
      if (params.type === "edit") {
        const data = params.data;
        setInputValues({
          logo: data?.logo || null,
          name_en: capitalizeText(data?.name?.en) || "",
          name_ro: capitalizeText(data?.name?.ro) || "",
          description_en: data?.description?.en || "",
          description_ro: data?.description?.ro || "",
          websiteUrl: data?.website || "",
          order: data?.order || 0,
        });
      }
    },

    closeDialog() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setInputValues(initialInputValues);
    setInputError({});
    setLogoFile(null);
    setIsLogoChanged(false);
  };

  const handleUploadImage = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (
      !["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(
        file.type,
      )
    ) {
      handleOpenSnackBar(
        "error",
        t("error.invalidImageFormat", { ns: "common" }),
      );
      event.target.value = null; 
      return;
    }

    // Revoke previous blob URL if it exists to avoid memory leaks
    if (inputValues.logo?.startsWith("blob:")) {
      URL.revokeObjectURL(inputValues.logo);
    }

    const previewUrl = URL.createObjectURL(file);

    setLogoFile(file);
    setIsLogoChanged(true);
    setInputValues((prev) => ({ ...prev, logo: previewUrl }));
    setInputError((prev) => ({ ...prev, logo: "" }));
    openLogoFile.current.value = null;
  };

  const handleRemoveLogo = () => {
    if (inputValues.logo?.startsWith("blob:")) {
      URL.revokeObjectURL(inputValues.logo);
    }
    setInputValues((prev) => ({ ...prev, logo: null }));
    setLogoFile(null);
    setIsLogoChanged(true);
  };

  const uploadLogoIfNeeded = async () => {
    if (!isLogoChanged) {
      return inputValues.logo;
    }

    if (isLogoChanged && !logoFile) {
      return null;
    }

    try {
      setIsLoading((prev) => ({ ...prev, logo: true }));
      const formData = new FormData();
      formData.append("image", logoFile);

      const response = await axios({
        method: "post",
        url: `${baseUrl}image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      return response?.data?.url;
    } catch (error) {
      handleOpenSnackBar(
        "error",
        t("error.imageUploadFailed", { ns: "common" }),
      );
      throw error;
    } finally {
      setIsLoading((prev) => ({ ...prev, logo: false }));
    }
  };

  const handleAddEdit = async () => {
    const valid = addEditCharityOrganizationValidation(
      inputValues,
      setInputError,
    );

    if (valid) {
      return handleOpenSnackBar(
        "error",
        t("resolveTheIssues", { ns: "common" }),
      );
    }

    try {
      setIsLoading((prev) => ({ ...prev, save: true }));

      const logoUrl = await uploadLogoIfNeeded();

      let payload = {
        logo: logoUrl,
        name: {
          en: inputValues.name_en,
          ro: inputValues.name_ro,
        },
        description: {
          en: inputValues.description_en,
          ro: inputValues.description_ro,
        },
        order: inputValues.order,
        website: inputValues.websiteUrl,
      };

      if (paramsData?.type === "add") {
        handleApiCall(createCharityOrganization, payload, "");
      } else {
        handleApiCall(
          updateCharityOrganization,
          payload,
          paramsData?.data?._id,
        );
      }
    } catch (error) {
    } finally {
      setIsLoading((prev) => ({ ...prev, save: false }));
    }
  };

  const handleApiCall = async (apiFunction, payload, id) => {
    try {
      const response = await apiFunction(id, payload);
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response?.data?.message);
        handleClose();
        if (paramsData?.type === "add") {
          dispatch(setCharityOrganizationCurrentPage(1));
        }
        paramsData?.getData(
          searchKeys.charityOrganizationName,
          pagination.page,
          pagination.limit,
        );
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar(
        "error",
        t("error.somethingWentWrong", { ns: "common" }),
      );
    }
  };

  const handleTranslateToEnglish = async (key1, key2) => {
    setIsLoading((prev) => ({ ...prev, translateToEnglish: true }));
    try {
      const [englishText] = await Promise.all([
        translateText(inputValues[key1]),
      ]);
      setInputValues((prev) => ({
        ...prev,
        [key2]: englishText,
      }));
      setInputError((prev) => ({
        ...prev,
        [key2]: "",
      }));
    } catch (error) {
      handleOpenSnackBar(
        "error",
        t("error.errorWhileTranslating", { ns: "common" }),
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, translateToEnglish: false }));
    }
  };

  const handleChageInputs = (e) => {
    const { name, value, valueAsNumber, type } = e.target;
    const formatedValue = type === "number" ? valueAsNumber : value;

    if (name === "page") {
      setInputValues((pre) => ({ ...pre, positionType: "" }));
    }

    setInputValues((pre) => ({ ...pre, [name]: formatedValue }));
    setInputError((pre) => ({ ...pre, [name]: "" }));
  };

  return (
    <>
      <AppLoader isVisible={isLoading.save} />
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        // fullWidth
        // maxWidth="md"
      >
        <DialogHeader
          title={
            paramsData.type === "add"
              ? t("addCharityOrganization")
              : t("editCharityOrganization")
          }
          secondaryHeading={
            paramsData.type === "add"
              ? t("addCharityOrganizationInstruction")
              : t("editCharityOrganizationInstruction")
          }
          icon={<TvIcon />}
          onClose={handleClose}
        />

        <DialogBody>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <UploadBox
                inputRef={openLogoFile}
                label={t("image", { ns: "common" })}
                // note={t("smallIconSizeNote", { ns: "common" })}
                value={inputValues.logo}
                error={inputError.logo}
                loading={isLoading.logo}
                placeholderImage={APP_IMAGES.uploadImage}
                accept="image/png, image/jpeg ,image/jpg ,image/webp"
                height={{ xs: 200, md: 130 }}
                previewSize={{ xs: 160, md: 180 }}
                onClick={() => openLogoFile.current.click()}
                onChange={handleUploadImage}
                onRemove={handleRemoveLogo}
              />
            </Grid>
            <Grid item xs={12}>
              <Label
                required
                labelText={t("order")}
                htmlFor={"charity-organization-sorting-number"}
              />
              <TextInput
                id={"charity-organization-sorting-number"}
                required
                placeholder={t("enterOrder")}
                name={"order"}
                type={"number"}
                value={inputValues.order}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.order)}
                helperText={t(inputError.order)}
              />
            </Grid>
            <Grid item xs={12}>
              <Label
                labelText={t("websiteUrl")}
                htmlFor={"charity-organization-website-url"}
                optional={true}
              />
              <TextInput
                id={"charity-organization-website-url"}
                placeholder={t("enterWebsiteUrl")}
                name={"websiteUrl"}
                type={"text"}
                value={inputValues.websiteUrl}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.websiteUrl)}
                helperText={t(inputError.websiteUrl)}
              />
            </Grid>
            <Grid item xs={12}>
              <Label
                required
                labelText={t("nameInRomanian")}
                htmlFor={`charity-organization-name-ro`}
              />
              <TextInput
                id={`charity-organization-name-ro`}
                required
                placeholder={t("enterNameInRomanian")}
                name={`name_ro`}
                textLength={200}
                value={inputValues.name_ro}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.name_ro)}
                helperText={t(inputError.name_ro)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.name_ro?.length}/200
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Stack
                mb={"5px"}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label
                  required
                  labelText={t("nameInEnglish")}
                  htmlFor={`charity-organization-name-en`}
                  labelProps={{ marginBottom: 0 }}
                />
                <Chip
                  label={t("translateToEnglish", { ns: "common" })}
                  size="small"
                  onClick={() => handleTranslateToEnglish("name_ro", "name_en")}
                  icon={
                    isLoading.translateToEnglish ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TranslateIcon />
                    )
                  }
                  disabled={isLoading.translateToEnglish}
                />
              </Stack>
              <TextInput
                id={`charity-organization-name-en`}
                required
                placeholder={t("enterNameInEnglish")}
                name={`name_en`}
                textLength={200}
                value={inputValues.name_en}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.name_en)}
                helperText={t(inputError.name_en)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.name_en?.length}/200
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Label
                required
                labelText={t("descriptionInRomanian")}
                htmlFor={`charity-organization-description-ro`}
              />
              <TextInput
                id={`charity-organization-description-ro`}
                required
                placeholder={t("enterDescriptionInRomanian")}
                name={`description_ro`}
                // textLength={300}
                value={inputValues.description_ro}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.description_ro)}
                helperText={t(inputError.description_ro)}
                multiline={true}
                rows={4}
                // InputEndIcon={
                //     <Typography variant="body2" color="text.grey">
                //         {inputValues.description_ro?.length}/300
                //     </Typography>
                // }
              />
            </Grid>
            <Grid item xs={12}>
              <Stack
                mb={"5px"}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label
                  required
                  labelText={t("descriptionInEnglish")}
                  htmlFor={`charity-organization-description-en`}
                  labelProps={{ marginBottom: 0 }}
                />
                <Chip
                  label={t("translateToEnglish", { ns: "common" })}
                  size="small"
                  onClick={() =>
                    handleTranslateToEnglish("description_ro", "description_en")
                  }
                  icon={
                    isLoading.translateToEnglish ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TranslateIcon />
                    )
                  }
                  disabled={isLoading.translateToEnglish}
                />
              </Stack>
              <TextInput
                id={`charity-organization-description-en`}
                required
                placeholder={t("enterDescriptionInEnglish")}
                name={`description_en`}
                // textLength={300}
                value={inputValues.description_en}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.description_en)}
                helperText={t(inputError.description_en)}
                multiline={true}
                rows={4}
                // InputEndIcon={
                //     <Typography variant="body2" color="text.grey">
                //         {inputValues.description_en?.length}/300
                //     </Typography>
                // }
              />
            </Grid>
          </Grid>
        </DialogBody>
        <DialogActionButtons
          onConfirm={handleAddEdit}
          onCancel={handleClose}
          confirmText={"save"}
          cancelText={"cancel"}
          isConfirmBtnDisable={isLoading.save}
          showCancelButton={true}
        />
      </BootstrapDialog>
    </>
  );
});
